import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const GuestHistoryModal = ({ isOpen, onClose, guest }) => {
  const [activeTab, setActiveTab] = useState('visits');

  if (!isOpen || !guest) return null;

  // Mock detailed visit history
  const visitHistory = [
  {
    id: 1,
    eventName: "Holiday Dinner Party",
    date: "2024-10-15",
    dishesServed: [
    "Herb-Crusted Salmon",
    "Roasted Vegetables",
    "Chocolate Tart"],

    notes: "Loved the salmon preparation. Asked for the recipe.",
    rating: 5,
    photos: [
    {
      url: "https://images.unsplash.com/photo-1603073162976-971c3f836d35",
      alt: "Elegant dinner table setting with herb-crusted salmon and roasted vegetables"
    }]

  },
  {
    id: 2,
    eventName: "Summer BBQ",
    date: "2024-08-22",
    dishesServed: [
    "Grilled Chicken",
    "Corn Salad",
    "Berry Cobbler"],

    notes: "Mentioned they\'re trying to eat less red meat. Enjoyed the vegetarian sides.",
    rating: 4,
    photos: []
  },
  {
    id: 3,
    eventName: "Birthday Celebration",
    date: "2024-06-10",
    dishesServed: [
    "Pasta Primavera",
    "Caesar Salad",
    "Birthday Cake"],

    notes: "First time hosting them. Very appreciative and easy-going guest.",
    rating: 5,
    photos: [
    {
      url: "https://images.unsplash.com/photo-1533683723796-13f0a97a5c17",
      alt: "Colorful pasta primavera with fresh vegetables and herbs on white plate"
    }]

  }];


  const preferences = [
  {
    category: "Favorite Cuisines",
    items: ["Mediterranean", "Asian Fusion", "Italian"]
  },
  {
    category: "Cooking Methods",
    items: ["Grilled", "Roasted", "Fresh/Raw"]
  },
  {
    category: "Flavors",
    items: ["Herb-forward", "Light & Fresh", "Citrusy"]
  },
  {
    category: "Dislikes",
    items: ["Overly spicy food", "Heavy cream sauces"]
  }];


  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) =>
    <Icon
      key={i}
      name="Star"
      size={14}
      className={i < rating ? 'text-warning fill-current' : 'text-muted-foreground'} />

    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-warm-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <Image
              src={guest?.avatar}
              alt={guest?.avatarAlt}
              className="w-12 h-12 rounded-full object-cover" />

            <div>
              <h2 className="text-xl font-semibold text-foreground">{guest?.name}</h2>
              <p className="text-sm text-muted-foreground">{guest?.email}</p>
              {guest?.familyGroup &&
              <p className="text-xs text-primary font-medium">{guest?.familyGroup}</p>
              }
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('visits')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'visits' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
              }>

              Visit History ({visitHistory?.length})
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'preferences' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
              }>

              Preferences & Notes
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Visit History Tab */}
          {activeTab === 'visits' &&
          <div className="space-y-6">
              {visitHistory?.map((visit) =>
            <div key={visit?.id} className="bg-muted/30 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{visit?.eventName}</h3>
                      <p className="text-sm text-muted-foreground">{formatDate(visit?.date)}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      {renderStars(visit?.rating)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-2">Dishes Served</h4>
                      <ul className="space-y-1">
                        {visit?.dishesServed?.map((dish, index) =>
                    <li key={index} className="flex items-center space-x-2 text-sm">
                            <Icon name="ChefHat" size={14} className="text-primary" />
                            <span>{dish}</span>
                          </li>
                    )}
                      </ul>
                    </div>

                    {visit?.photos?.length > 0 &&
                <div>
                        <h4 className="text-sm font-medium text-foreground mb-2">Photos</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {visit?.photos?.map((photo, index) =>
                    <div key={index} className="aspect-square rounded-lg overflow-hidden">
                              <Image
                        src={photo?.url}
                        alt={photo?.alt}
                        className="w-full h-full object-cover" />

                            </div>
                    )}
                        </div>
                      </div>
                }
                  </div>

                  {visit?.notes &&
              <div className="mt-4 p-3 bg-card rounded-lg border border-border">
                      <h4 className="text-sm font-medium text-foreground mb-1">Notes</h4>
                      <p className="text-sm text-muted-foreground">{visit?.notes}</p>
                    </div>
              }
                </div>
            )}

              {visitHistory?.length === 0 &&
            <div className="text-center py-12">
                  <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Visit History</h3>
                  <p className="text-muted-foreground">
                    This guest hasn't attended any dinner events yet.
                  </p>
                </div>
            }
            </div>
          }

          {/* Preferences Tab */}
          {activeTab === 'preferences' &&
          <div className="space-y-6">
              {/* Dietary Restrictions */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Dietary Restrictions</h3>
                {guest?.dietaryRestrictions && guest?.dietaryRestrictions?.length > 0 ?
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {guest?.dietaryRestrictions?.map((restriction, index) =>
                <div key={index} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
                        <div className="flex items-center space-x-2">
                          <span className={`w-2 h-2 rounded-full ${
                    restriction?.type === 'allergy' ? 'bg-error' :
                    restriction?.type === 'intolerance' ? 'bg-warning' : 'bg-secondary'}`
                    } />
                          <span className="font-medium">{restriction?.name}</span>
                          <span className="text-xs text-muted-foreground capitalize">({restriction?.type})</span>
                        </div>
                        {restriction?.severity &&
                  <span className="text-xs px-2 py-1 bg-muted rounded-full">
                            {restriction?.severity}
                          </span>
                  }
                      </div>
                )}
                  </div> :

              <p className="text-muted-foreground">No dietary restrictions recorded.</p>
              }
              </div>

              {/* Learned Preferences */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Learned Preferences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {preferences?.map((category, index) =>
                <div key={index} className="bg-muted/30 p-4 rounded-lg">
                      <h4 className="font-medium text-foreground mb-3">{category?.category}</h4>
                      <div className="space-y-2">
                        {category?.items?.map((item, itemIndex) =>
                    <div key={itemIndex} className="flex items-center space-x-2">
                            <Icon
                        name={category?.category === 'Dislikes' ? 'X' : 'Check'}
                        size={14}
                        className={category?.category === 'Dislikes' ? 'text-error' : 'text-success'} />

                            <span className="text-sm">{item}</span>
                          </div>
                    )}
                      </div>
                    </div>
                )}
                </div>
              </div>

              {/* Additional Notes */}
              {guest?.notes &&
            <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Additional Notes</h3>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <p className="text-muted-foreground">{guest?.notes}</p>
                  </div>
                </div>
            }
            </div>
          }
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}>

            Close
          </Button>
          <Button
            variant="default"
            iconName="Edit2"
            iconPosition="left">

            Edit Guest
          </Button>
        </div>
      </div>
    </div>);

};

export default GuestHistoryModal;